import { z } from 'zod';
import type { ClinicianIdentityProofingRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { dobField, phoneField, zipField } from '@/shared/lib/validators';

/**
 * Form values shape for the demographic info step.
 * Wraps the SDK type to match mutation input structure.
 */
export interface DemographicInfoFormValues {
  demographicInfo: ClinicianIdentityProofingRequestT;
}

/**
 * Schema for ClinicianIdentityProofingRequestT with field-level validation messages.
 * Includes secure handling for SSN/CC fields.
 */
export const demographicInfoSchema: z.ZodType<ClinicianIdentityProofingRequestT> = z.object({
  FirstName: z.string().trim().min(1, 'First name is required'),
  LastName: z.string().trim().min(1, 'Last name is required'),
  DateOfBirth: dobField,
  HomeAddress: z
    .string()
    .trim()
    .min(1, 'Home address is required')
    .regex(/^[A-Za-z0-9 /.\-#]+$/, 'Address allows only letters, digits, and / . - # spaces'),
  City: z.string().trim().min(1, 'City is required'),
  State: z
    .string()
    .trim()
    .min(1, 'State is required')
    .max(2, 'State must be a 2-letter abbreviation'),
  ZipCode: zipField,
  PhoneNumber: phoneField,
  IsMobileNumber: z.boolean(),
  SocialSecurityNumber: z
    .string()
    .trim()
    .min(1, 'Social Security Number is required')
    .regex(/^\d{9}$/, 'SSN must be exactly 9 digits (no dashes)'),
  CreditCardNumber: z
    .string()
    .trim()
    .min(1, 'Credit card number is required')
    .regex(/^\d+$/, 'Credit card number must contain only digits'),
  MiddleName: z.string().trim().nullable().optional(),
  DrivingLicenseNumber: z.string().trim().nullable().optional(),
  DrivingLicenseNumberState: z.string().trim().nullable().optional(),
});

/**
 * Schema for the full demographic info form values.
 */
export const demographicInfoFormSchema: z.ZodType<DemographicInfoFormValues> = z.object({
  demographicInfo: demographicInfoSchema,
});

/** Zod safe parse success result for demographic info. */
export type ValidateDemographicSuccess = { success: true; data: DemographicInfoFormValues };

/** Zod safe parse error result for demographic info. */
export type ValidateDemographicError = {
  success: false;
  error: z.ZodError<DemographicInfoFormValues>;
};

/** Zod safe parse result for validateDraft. */
export type ValidateDemographicResult = ValidateDemographicSuccess | ValidateDemographicError;

/**
 * Create empty demographic info with sensible defaults.
 */
export function createEmptyDemographicInfo(): ClinicianIdentityProofingRequestT {
  return {
    FirstName: '',
    LastName: '',
    MiddleName: null,
    DateOfBirth: '',
    HomeAddress: '',
    City: '',
    State: '',
    ZipCode: '',
    PhoneNumber: '',
    IsMobileNumber: true,
    SocialSecurityNumber: '',
    CreditCardNumber: '',
    DrivingLicenseNumber: null,
    DrivingLicenseNumberState: null,
  };
}
